import type { ContentDatabase as NativeContentDatabase } from "@-/dev.improve.haniverse.database.content";
import type { StaticDatabase as NativeStaticDatabase } from "@-/dev.improve.haniverse.database.static";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import { hasLength } from "@-/com.npmjs.ts-array-length";

export type MorphemeDetailsTileSliceLookup = { morpheme: string } | { id: number };

// A meaning card with its pronunciation cards, fetched in one relational query.
type MeaningCardWithPronunciations = {
  back: string | null;
  completedAt: string | null;
  description: string | null;
  grapheme: string;
  id: number;
  label: string | null;
  mnemonicTerms: string[];
  obsolete: boolean;
  pronunciationCards: {
    gloss: string;
    phrase: string | null;
    pinyin: string;
  }[];
};

// A static grapheme with its frequency / level info data, fetched in one
// relational query.
type GraphemeWithRefData = {
  biau: { rank: number } | null;
  glyph: string;
  prcData: {
    minHsk2010Level: number | null;
    minHsk2021Level: number | null;
    minHsk2026Level: number | null;
    tier: number | null;
  } | null;
  subtlex: { rank: number } | null;
  twData: {
    tbclLevel: number | null;
    usageClassification: "common" | "less-than-common" | "rarely-used" | null;
  } | null;
};

function toMorphemeTier(tier: number | null | undefined): MorphemeDetailsTileSlice["tier"] {
  if (tier === 1 || tier === 2 || tier === 3) {
    return tier;
  }
  return null;
}

function buildMorphemeDetailsTileCard(
  card: MeaningCardWithPronunciations,
  grapheme: GraphemeWithRefData | undefined,
): MorphemeDetailsTileSlice {
  const prcData = grapheme?.prcData ?? undefined;
  return {
    back: card.back ?? undefined,
    biau1Rank: grapheme?.biau?.rank ?? null,
    completed: card.completedAt !== null ? true : undefined,
    description: card.description,
    front: card.grapheme,
    hsk2010Level: prcData?.minHsk2010Level ?? null,
    hsk2021Level: prcData?.minHsk2021Level ?? null,
    hsk2026Level: prcData?.minHsk2026Level ?? null,
    id: card.id,
    label: card.label,
    mnemonicTerms: card.mnemonicTerms,
    morphemeType: "grapheme",
    obsolete: card.obsolete,
    pinyinList: card.pronunciationCards.map((p) => ({
      gloss: p.gloss,
      mnemonic: p.phrase ?? undefined,
      toneNumbersPinyin: p.pinyin,
    })),
    subtlexChrRank: grapheme?.subtlex?.rank ?? null,
    taiwanUsageClassification: grapheme?.twData?.usageClassification ?? null,
    tbclLevel: grapheme?.twData?.tbclLevel ?? null,
    tier: toMorphemeTier(prcData?.tier),
  };
}

export async function getMorphemeDetailsTileSlices(
  contentDb: ContentDatabase,
  staticDb: StaticDatabase,
  lookups: readonly MorphemeDetailsTileSliceLookup[],
): Promise<(MorphemeDetailsTileSlice | null)[]> {
  if (hasLength(lookups, 0)) {
    return [];
  }

  const requestedMorphemes = uniq(lookups.flatMap((l) => ("morpheme" in l ? [l.morpheme] : [])));
  const requestedIds = uniq(lookups.flatMap((l) => ("id" in l ? [l.id] : [])));

  // An id-based lookup may target a meaning or a pronunciation card; both map
  // to the same grapheme preview. Resolve each id to its grapheme via the
  // matching front table.
  let lookupGraphemeRows: { cardId: number; grapheme: string }[];
  if (hasLength(requestedIds, 0)) {
    lookupGraphemeRows = [];
  } else {
    const [meaningLookupRows, pronunciationLookupRows] = await Promise.all([
      contentDb(import.meta.location).query.appGraphemeMeaningCard.findMany({
        columns: { grapheme: true, id: true },
        where: { id: { in: requestedIds } },
      }),
      contentDb(import.meta.location).query.appGraphemePronunciationCard.findMany({
        columns: { grapheme: true, id: true },
        where: { id: { in: requestedIds } },
      }),
    ]);
    lookupGraphemeRows = [...meaningLookupRows, ...pronunciationLookupRows].map((r) => ({
      cardId: r.id,
      grapheme: r.grapheme,
    }));
  }
  const frontByLookupId = new Map(lookupGraphemeRows.map((r) => [r.cardId, r.grapheme]));

  const allMorphemesToFetch = uniq([
    ...requestedMorphemes,
    ...lookupGraphemeRows.map((r) => r.grapheme),
  ]);

  // Pull each meaning card together with its pronunciation cards in a single
  // relational query (joined on `grapheme`).
  const meaningCardRows: MeaningCardWithPronunciations[] = hasLength(allMorphemesToFetch, 0)
    ? []
    : await contentDb(import.meta.location)
        .query.appGraphemeMeaningCard.findMany({
          columns: {
            completedAt: true,
            gloss: true,
            grapheme: true,
            id: true,
            label: true,
            obsolete: true,
          },
          where: { grapheme: { in: allMorphemesToFetch } },
          with: {
            aliases: { columns: { alias: true }, orderBy: { position: "asc" } },
            description: { columns: { mnemonic: true } },
            pronunciationCards: {
              columns: { gloss: true, phrase: true, pinyin: true },
            },
          },
        })
        .then((rows) =>
          rows.map((row) => ({
            back: row.gloss,
            completedAt: row.completedAt,
            description: row.description?.mnemonic ?? null,
            grapheme: row.grapheme,
            id: row.id,
            label: row.label,
            // Same order as the panel heading: the gloss leads when it is itself a mnemonic.
            mnemonicTerms: row.aliases
              .map(({ alias }) => alias)
              .filter((alias) => alias.trim() !== "")
              .sort((left, right) => Number(right === row.gloss) - Number(left === row.gloss)),
            obsolete: row.obsolete,
            pronunciationCards: row.pronunciationCards,
          })),
        );

  const cardByFront = new Map(meaningCardRows.map((c) => [c.grapheme, c]));
  const allFronts = uniq(meaningCardRows.map((c) => c.grapheme));

  // Pull each static grapheme together with its frequency / level info
  // data in a single relational query.
  const graphemeRows: GraphemeWithRefData[] = hasLength(allFronts, 0)
    ? []
    : await staticDb(import.meta.location).query.grapheme.findMany({
        columns: { glyph: true },
        where: { glyph: { in: allFronts } },
        with: {
          biau: { columns: { rank: true } },
          prcData: {
            columns: {
              minHsk2010Level: true,
              minHsk2021Level: true,
              minHsk2026Level: true,
              tier: true,
            },
          },
          subtlex: { columns: { rank: true } },
          twData: { columns: { tbclLevel: true, usageClassification: true } },
        },
      });

  const graphemeByGlyph = new Map<string, GraphemeWithRefData>();
  for (const g of graphemeRows) {
    if (!graphemeByGlyph.has(g.glyph)) {
      graphemeByGlyph.set(g.glyph, g);
    }
  }

  // Anything requested that has NO grapheme meaning card may still be a WORD (the deck holds word
  // cards alongside character cards). Without this the lookup returns null for every word and the
  // caller's `filterNullable` drops it silently — which is exactly how word cards went missing
  // from the deck while their `user_card` rows existed and were scheduled.
  const unresolvedMorphemes = requestedMorphemes.filter((morpheme) => !cardByFront.has(morpheme));
  const wordRows = hasLength(unresolvedMorphemes, 0)
    ? []
    : await staticDb(import.meta.location).query.word.findMany({
        columns: { id: true, word: true },
        where: { word: { in: unresolvedMorphemes } },
        with: {
          definitions: { columns: { definition: true, pinyin: true } },
          subtlex: { columns: { rank: true } },
        },
      });
  const wordByForm = new Map(wordRows.map((row) => [row.word, row]));

  return lookups.map((lookup) => {
    let card: MeaningCardWithPronunciations | undefined;
    if ("morpheme" in lookup) {
      card = cardByFront.get(lookup.morpheme);
    } else {
      const front = frontByLookupId.get(lookup.id);
      if (front !== undefined) {
        card = cardByFront.get(front);
      }
    }
    if (card === undefined) {
      // Fall back to the word variant before giving up (see `wordByForm` above).
      if ("morpheme" in lookup) {
        const wordRow = wordByForm.get(lookup.morpheme);
        if (wordRow !== undefined) {
          const primaryDefinition = wordRow.definitions[0];
          return {
            back: primaryDefinition?.definition ?? undefined,
            front: wordRow.word,
            id: wordRow.id,
            morphemeType: "word",
            pinyinList:
              primaryDefinition?.pinyin === undefined || primaryDefinition.pinyin === null
                ? []
                : [
                    {
                      gloss: primaryDefinition.definition ?? "",
                      toneNumbersPinyin: primaryDefinition.pinyin,
                    },
                  ],
            subtlexWfRank: wordRow.subtlex?.rank ?? null,
            tier: null,
          } satisfies MorphemeDetailsTileSlice;
        }
      }
      return null;
    }
    return buildMorphemeDetailsTileCard(card, graphemeByGlyph.get(card.grapheme));
  });
}

type ContentDatabase = (
  location: Parameters<NativeContentDatabase>[0],
) => Pick<ReturnType<NativeContentDatabase>, "query" | "select">;
type StaticDatabase = (
  location: Parameters<NativeStaticDatabase>[0],
) => Pick<ReturnType<NativeStaticDatabase>, "query" | "select" | "all">;
